import React, { useEffect, useState } from 'react';
import { EmojiReaction } from '../types';

interface EmojiAnimationProps {
  reactions: EmojiReaction[];
  onComplete: (reactionId: string) => void;
}

interface AnimatedEmoji extends EmojiReaction {
  left: number; // Random horizontal position (0-100%)
}

export const EmojiAnimation: React.FC<EmojiAnimationProps> = ({
  reactions,
  onComplete,
}) => {
  const [animatedEmojis, setAnimatedEmojis] = useState<AnimatedEmoji[]>([]);

  // Add new reactions to animated emojis with random positions
  useEffect(() => {
    const newEmojis = reactions
      .filter(r => !animatedEmojis.some(a => a.id === r.id))
      .map(reaction => ({
        ...reaction,
        left: Math.random() * 80 + 10, // Random position between 10% and 90%
      }));

    if (newEmojis.length > 0) {
      setAnimatedEmojis(prev => [...prev, ...newEmojis]);

      // Set timeout to remove emojis after animation completes
      newEmojis.forEach(emoji => {
        setTimeout(() => {
          setAnimatedEmojis(prev => prev.filter(e => e.id !== emoji.id));
          onComplete(emoji.id);
        }, 3000); // Animation duration: 3 seconds
      });
    }
  }, [reactions, animatedEmojis, onComplete]);

  if (animatedEmojis.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {animatedEmojis.map((emoji) => (
        <div
          key={emoji.id}
          className="absolute bottom-0 animate-float-up"
          style={{
            left: `${emoji.left}%`,
            animation: 'float-up 3s ease-out forwards',
          }}
        >
          <div className="text-4xl animate-pulse">
            {emoji.emoji}
          </div>
        </div>
      ))}
      <style>{`
        @keyframes float-up {
          0% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
          50% {
            transform: translateY(-50vh) scale(1.2);
            opacity: 0.8;
          }
          100% {
            transform: translateY(-100vh) scale(0.8);
            opacity: 0;
          }
        }
        .animate-float-up {
          animation: float-up 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};
